/**
 * Stores > Store content at /stores/content.
 *
 * HQ editor console for the Content module (api#343 / migration 0096). Lists
 * every content item across all statuses; row click opens the editor. The
 * Sweet Spot store site renders published items read-only; this page is the
 * ONLY place they are authored, curated, or approved.
 *
 * Filters: area, status (most-actionable first: Submitted, Draft, Published,
 * Archived), server-side search (?q=). A "Submissions" quick chip flips the
 * status to submitted so curators find the recognition/ideas queue in one click.
 * Past-due review_date is flagged red in the Review by column.
 *
 * Gated by content:admin.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { formatDate, formatDateTime } from '@/lib/dateUtils';
import { useSession } from '@/lib/useSession';
import {
  CONTENT_AREAS,
  CONTENT_AREA_LABELS,
  CONTENT_STATUS_FILTER_ORDER,
  CONTENT_STATUS_LABELS,
  hasContentAdmin,
  isReviewPastDue,
  localTodayIso,
  useContentItems,
  type ContentArea,
  type ContentItem,
  type ContentStatus,
} from '@/lib/useContent';
import { AccessDeniedCard, ErrorCard, StoresHeader } from './storesShared';

// Status pill palette per the issue spec:
//   draft = neutral, submitted = warning, published = positive, archived = muted.
// Mirrors the STATUS_TONES pattern used in Commerce > Orders (COCKPIT.md 2.9).
const STATUS_TONES: Record<ContentStatus, string> = {
  draft: 'bg-amp-cream-warm text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink',
  submitted: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  published: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
};

function ContentStatusPill({ status }: { status: ContentStatus }) {
  return <Pill tone={STATUS_TONES[status]}>{CONTENT_STATUS_LABELS[status]}</Pill>;
}

// Client-side sort. The list endpoint returns every matching item in one
// response; sorting stays in the browser (same pattern as StoreUsers).
const SORT_COLUMNS = ['title', 'area', 'status', 'owner', 'review', 'updated'] as const;
type SortColumn = (typeof SORT_COLUMNS)[number];

function parseSortParam(raw: string | null): { column: SortColumn; dir: SortDir } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!SORT_COLUMNS.includes(col as SortColumn)) return null;
  return { column: col as SortColumn, dir: dir === 'desc' ? 'desc' : 'asc' };
}

function sortValue(row: ContentItem, col: SortColumn): string {
  switch (col) {
    case 'title':
      return row.title.toLowerCase();
    case 'area':
      return row.area;
    case 'status':
      return row.status;
    case 'owner':
      return (row.owner_email ?? '').toLowerCase();
    case 'review':
      return row.review_date ?? '';
    case 'updated':
      return row.updated_at ?? '';
  }
}

function sortRows(rows: ContentItem[], col: SortColumn, dir: SortDir): ContentItem[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, col);
    const bv = sortValue(b, col);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
}

const inputClass =
  'rounded-md border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink ' +
  'placeholder:text-amp-ink-muted focus:border-amp-olive focus:outline-none ' +
  'dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted';

export function ContentList() {
  const { user } = useSession();
  const canView = hasContentAdmin(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const rawArea = searchParams.get('area') as ContentArea | null;
  const rawStatus = searchParams.get('status') as ContentStatus | null;
  const searchInput = searchParams.get('q') ?? '';
  const area: ContentArea | undefined = rawArea && CONTENT_AREAS.includes(rawArea) ? rawArea : undefined;
  const status: ContentStatus | undefined =
    rawStatus && CONTENT_STATUS_FILTER_ORDER.includes(rawStatus) ? rawStatus : undefined;

  const setParam = (key: string, value: string | undefined) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value == null || value === '') next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  // Debounce the search input so we do not fire on every keystroke. The URL
  // stays authoritative; typing writes ?q= after 250ms.
  const [searchDraft, setSearchDraft] = useState(searchInput);
  useEffect(() => setSearchDraft(searchInput), [searchInput]);
  useEffect(() => {
    if (searchDraft === searchInput) return;
    const t = window.setTimeout(() => setParam('q', searchDraft || undefined), 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const filters = useMemo(() => ({ area, status, q: searchInput }), [area, status, searchInput]);
  const itemsQ = useContentItems(canView ? filters : { area: undefined, status: undefined });

  const sort = parseSortParam(searchParams.get('sort'));
  const sortBy = sort?.column ?? 'updated';
  const sortDir: SortDir = sort?.dir ?? 'desc';
  const toggleSort = (col: SortColumn) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (sortBy !== col) next.set('sort', `${col}:asc`);
        else if (sortDir === 'asc') next.set('sort', `${col}:desc`);
        else next.delete('sort');
        return next;
      },
      { replace: true },
    );
  };

  if (user && !canView) return <AccessDeniedCard what="Store content" />;

  const rows = sortRows(itemsQ.data?.items ?? [], sortBy, sortDir);
  const today = localTodayIso();
  const submissionsActive = status === 'submitted';

  const toolbar = (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[200px] max-w-md flex-1">
        <FilterLabel htmlFor="content-search">Search</FilterLabel>
        <input
          id="content-search"
          type="text"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          placeholder="Title or body..."
          className={`${inputClass} w-full`}
        />
      </div>

      <div>
        <FilterLabel htmlFor="content-area">Area</FilterLabel>
        <select
          id="content-area"
          value={area ?? ''}
          onChange={(e) => setParam('area', e.target.value || undefined)}
          className={inputClass}
        >
          <option value="">All areas</option>
          {CONTENT_AREAS.map((a) => (
            <option key={a} value={a}>
              {CONTENT_AREA_LABELS[a]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <FilterLabel htmlFor="content-status">Status</FilterLabel>
        <select
          id="content-status"
          value={status ?? ''}
          onChange={(e) => setParam('status', e.target.value || undefined)}
          className={inputClass}
        >
          <option value="">All statuses</option>
          {CONTENT_STATUS_FILTER_ORDER.map((s) => (
            <option key={s} value={s}>
              {CONTENT_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {/* One-click Submissions filter: flips status=submitted so curators find
          the recognition / ideas queue in a single click (per issue). */}
      <Pill
        variant={submissionsActive ? 'selected' : 'outline'}
        aria-pressed={submissionsActive}
        onClick={() => setParam('status', submissionsActive ? undefined : 'submitted')}
      >
        Submissions
      </Pill>

      <div className="ml-auto">
        <Link
          to="/stores/content/new"
          className="inline-flex items-center gap-1 rounded bg-amp-olive-deep px-3 py-1.5 text-sm font-medium text-amp-cream hover:bg-amp-olive dark:bg-amp-olive dark:hover:bg-amp-olive-deep"
        >
          <Plus className="h-3 w-3" aria-hidden /> New page
        </Link>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <StoresHeader
        title="Store content"
        subtitle="Publish pages the Sweet Spot store site shows to store managers: promotions, resources, marketing, and store submissions."
      />

      <DataGridShell
        total={rows.length}
        loaded={rows.length}
        countNoun="pages"
        toolbar={toolbar}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader
                label="Title"
                sorted={sortBy === 'title' ? sortDir : null}
                onToggle={() => toggleSort('title')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Area"
                sorted={sortBy === 'area' ? sortDir : null}
                onToggle={() => toggleSort('area')}
                thClassName="px-3 py-2 w-[130px]"
              />
              <SortableHeader
                label="Status"
                sorted={sortBy === 'status' ? sortDir : null}
                onToggle={() => toggleSort('status')}
                thClassName="px-3 py-2 w-[130px]"
              />
              <SortableHeader
                label="Owner"
                sorted={sortBy === 'owner' ? sortDir : null}
                onToggle={() => toggleSort('owner')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Review by"
                sorted={sortBy === 'review' ? sortDir : null}
                onToggle={() => toggleSort('review')}
                thClassName="px-3 py-2 w-[140px]"
              />
              <SortableHeader
                label="Updated"
                sorted={sortBy === 'updated' ? sortDir : null}
                onToggle={() => toggleSort('updated')}
                thClassName="px-3 py-2 w-[160px]"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {itemsQ.isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading content...
                </td>
              </tr>
            ) : itemsQ.error ? (
              <tr>
                <td colSpan={6} className="px-4 py-6">
                  <ErrorCard message={itemsQ.error.message} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No content pages match these filters.
                </td>
              </tr>
            ) : (
              rows.map((item) => <ContentRow key={item.id} item={item} today={today} />)
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function ContentRow({ item, today }: { item: ContentItem; today: string }) {
  const pastDue = isReviewPastDue(item.review_date, today);
  return (
    <tr className="align-top hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      <td className="px-3 py-2">
        <Link
          to={`/stores/content/${item.id}`}
          className="font-medium text-amp-ink hover:underline dark:text-amp-dark-ink"
        >
          {item.title || '(untitled)'}
        </Link>
      </td>
      <td className="px-3 py-2">
        <Pill>{CONTENT_AREA_LABELS[item.area] ?? item.area}</Pill>
      </td>
      <td className="px-3 py-2">
        <ContentStatusPill status={item.status} />
      </td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        {item.owner_email ?? <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">—</span>}
      </td>
      <td className="px-3 py-2">
        {item.review_date ? (
          <span
            className={
              pastDue
                ? 'font-medium text-red-700 dark:text-red-300'
                : 'text-amp-ink dark:text-amp-dark-ink'
            }
            title={pastDue ? 'Review is past due' : undefined}
          >
            {formatDate(item.review_date)}
          </span>
        ) : (
          <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {formatDateTime(item.updated_at)}
      </td>
    </tr>
  );
}
